// ── metria ────────────────────────────────────────────────────────────────
// La famiglia del brand NUOVO: terracotta e vernice, sui colori di un oggetto
// vero. `brand500` chiaro e `brand100` discendono dai due valori CAMPIONATI su
// un'anfora panatenaica del 530 a.C. (argilla #ce703a, vernice #262021, media
// dei pixel sopra 110 e sotto 50 di luminanza) — poi spostati dove i cancelli
// di contrasto li volevano, che e' l'unico motivo per cui non sono identici.
//
// ⛔ NON e' la famiglia `brand`. Quella resta l'arancione storico di
// JackedLogs, che vive in design-tokens.ts ed e' ancora il default di
// molti percorsi non tematizzati: qui non si tocca, cambia solo la sua
// ETICHETTA (vedi THEME_FAMILIES), perche' la chiave e' il valore persistito
// in Profile.themePreference e rinominarla migrerebbe i dati dei tre atleti.
//
// Costruita CONTRO le asserzioni di apps/mobile/__tests__/paletteContrast.test.ts
// e effortRampContrast.test.ts, non a occhio: ogni coppia e' stata calcolata
// prima di scrivere questa riga. Margini che contano, e non sono raschiati
// (Antenore e' miope, il pavimento non e' un numero da sfiorare):
//   inkOnBrand su brand500  5.28 chiaro · 7.03 scuro
//   scala degli inchiostri su surface2  11.57 > 8.26 > 6.31 chiaro
//                                       13.28 > 9.42 > 6.48 scuro
//   borderStrong, minimo sulle tre superfici  4.29 chiaro · 3.52 scuro (floor 3)

import type { ThemeDefinition } from "./types.js";

export const metria: ThemeDefinition = {
  palette: {
    light: {
      brand500: "#ad4c1d", brand600: "#8f4116", brand100: "#f7e2d3",
      brandText: "#8f4116", brandPressed: "#8f4116", inkOnBrandPressed: "#fffaf4",
      bg: "#faf3ea", surface: "#fffaf4", surface2: "#f2e6d9",
      border: "#e6d6c5", borderStrong: "#7d6857", scrim: "rgba(0,0,0,0.5)",
      ink: "#33291f", ink2: "#4d3f31", ink3: "#5f5040", inkOnBrand: "#fffaf4",
      inkOnDanger: "#fffaf4", inkOnWarn: "#fffaf4",
      pr: "#7d5a12", prFill: "#f8ecd6",
      // `complete` e' il VERDERAME, non un verde qualunque, e la scelta e'
      // misurata su due assi. (1) Stessa luminanza del verde che sostituisce
      // (L* 38.2 contro 37.9): il ruolo e' anche INCHIOSTRO (SetCountRow.tsx,
      // RosterStrip.tsx lo usano come `color:`), quindi cambiargli peso
      // cambierebbe la gerarchia della pagina. Floor 4.5:1 tenuto su tutte e
      // tre le superfici + su `completeFill` (min 5.62). (2) Il vecchio
      // #2f6349 stava a **9.2** da `danger` sotto deuteranopia, cioe'
      // «completato» e «fallito» si somigliavano; il verderame sta a **17.5**.
      complete: "#3b6159", completeFill: "#e2edea",
      danger: "#93343a", dangerFill: "#f8e2df",
      warn: "#7d5a12", warnFill: "#f8ecd6",
    },
    dark: {
      brand500: "#e2894f", brand600: "#a85a2c", brand100: "#33231a",
      brandText: "#e2894f", brandPressed: "#a85a2c", inkOnBrandPressed: "#fdf3e9",
      bg: "#16120e", surface: "#1e1813", surface2: "#271f18",
      border: "#3b2f25", borderStrong: "#87715e", scrim: "rgba(0,0,0,0.5)",
      ink: "#f2e7d9", ink2: "#d3c3b0", ink3: "#b3a18d", inkOnBrand: "#16120e",
      inkOnDanger: "#16120e", inkOnWarn: "#16120e",
      pr: "#e6b45f", prFill: "#2d2317",
      // Stessa mossa in scuro: L* 71.4 contro i 73.4 del vecchio #7cc39c, e
      // separazione da `danger` che sale da **9.4** a **17.3** sotto
      // deuteranopia. Floor tenuto ovunque (min 7.10).
      complete: "#7cbbaa", completeFill: "#19241f",
      danger: "#e8827a", dangerFill: "#2e1b19",
      warn: "#e6b45f", warnFill: "#2d2317",
    },
  },
  // ⛔⛔ RITARATA due volte, e la seconda ha corretto la PRIMA misura.
  // 23/08: `moderate` e `heavy` stavano a 25 gradi di tinta, 25.9 di ΔE in
  // visione normale ma **8.1** sotto deuteranopia, sotto la soglia dei 10 dove
  // due colori smettono di distinguersi. Nessun test lo guardava.
  // 23/08 tardi: la misura di poche ore prima usava ΔE **CIE76** e guardava
  // solo le coppie ADIACENTI, quindi dichiarava 31.5 di margine mentre i due
  // ESTREMI della scala - `work` e `max`, i due che meno devono confondersi -
  // stavano a **7.4** in CIEDE2000. `work` era un verde freddo, `max` e' un
  // porpora: sotto deuteranopia diventano lo stesso grigio-blu. Ora `work` e'
  // la terra verde (verde caldo, giallo), che dal porpora si separa.
  // Numeri attuali, CIEDE2000 su TUTTE le coppie (non solo le adiacenti):
  //   chiaro 23.1 normale / 12.1 deuteranopia  ·  scuro 22.4 / 11.0
  // ⚠️ `moderate` chiaro porta l'inchiostro SCURO, non il bianco: spingendolo
  // verso l'oro il bianco cadeva a 3.05. Stessa mossa che rosepine fa gia'
  // sulla sua banda oro. Le bande restano nel registro di terra (terra verde,
  // oro, terracotta, vino), ma separate davvero.
  intensityScale: {
    light: {
      work: { fill: "#66734f", on: "#ffffff", soft: "#eaece0", text: "#33291f" },
      moderate: { fill: "#c99408", on: "#33291f", soft: "#f8ecd6", text: "#33291f" },
      heavy: { fill: "#a83c1c", on: "#ffffff", soft: "#f7e2d3", text: "#33291f" },
      max: { fill: "#7d3a52", on: "#ffffff", soft: "#f4e2e8", text: "#33291f" },
      none: { fill: "#f2e6d9", on: "#33291f", soft: "#f7efe6", text: "#5f5040" },
    },
    dark: {
      work: { fill: "#9dc492", on: "#16120e", soft: "#1f2a1c", text: "#f2e7d9" },
      moderate: { fill: "#efc352", on: "#16120e", soft: "#2d2317", text: "#f2e7d9" },
      heavy: { fill: "#e2894f", on: "#16120e", soft: "#33231a", text: "#f2e7d9" },
      max: { fill: "#d1728f", on: "#16120e", soft: "#2e1b24", text: "#f2e7d9" },
      none: { fill: "#271f18", on: "#f2e7d9", soft: "#221b15", text: "#b3a18d" },
    },
  },
  // ⛔⛔ RISCRITTA il 23/08, ed e' l'errore piu' istruttivo di questo file.
  // La prima versione diceva «nessun salto di famiglia cromatica» e copriva
  // **34 gradi** di tinta in chiaro e **8** in scuro, contro i 110-213 delle
  // sorelle: praticamente una tinta sola modulata in luminanza. Su una mappa
  // corpo, dove il colore E' l'informazione e non c'e' nessun glifo a
  // rinforzarlo (`BodyMap.tsx` via `WorkoutHeatMap.tsx`), «lavorato» e
  // «sovraccaricato» si leggevano come lo stesso arancione piu' scuro. Per un
  // miope, a distanza di braccio, non e' una sfumatura: e' un dato perso.
  //
  // ⭐ E la lezione, che vale oltre questo ruolo: la famiglia era stata
  // costruita CONTRO i cancelli automatici, e li' e' forte quanto o piu' delle
  // sorelle. Questo ruolo **nessun cancello lo guarda**, ed era il piu' debole
  // delle quattro. Costruire verso il rilevatore lascia scoperto tutto cio' che
  // il rilevatore non vede.
  //
  // La cura segue il precedente di rosepine («non ha un verde vero, quindi
  // parte fredda e si scalda»): qui parte dal **verderame del bronzo**, che sta
  // nel registro greco meglio di un verde qualunque, e sale a ocra, terracotta,
  // ossido.
  //
  // ⛔⛔ RI-MISURATA poche ore dopo, e la cura NON era completa: quei
  // numeri (27.7/25.4 e 20.7/15.5) sono ΔE **CIE76**, che sovrastima proprio
  // nella regione verde-giallo. In **CIEDE2000** la stessa rampa dava 8.8
  // (chiaro) e 7.8 (scuro) sulla coppia `#b0b86b`->`#e6ad3a`, cioe' verde-giallo
  // contro oro: sotto la soglia di 10 che il commento stesso dichiarava.
  // ⇒ Il gradino verde-giallo e' stato tolto. I due primi stop sono ora due
  // pigmenti veri scelti da Antenore (terra verde azzurrata e oliva), che si
  // separano per LUMINANZA - l'unico asse che la deuteranopia conserva insieme
  // al blu-giallo.
  // Peggior coppia adiacente, CIEDE2000: chiaro 19.7 normale / 15.2
  // deuteranopia · scuro 14.3 / 10.6.
  // ⚠️ Residuo dichiarato, non nascosto: il gradino oro sta a 1.78 di contrasto
  // WCAG contro `surface2`, che e' la superficie che conta davvero - BodyMap
  // dipinge con `surface2` i muscoli NON lavorati, quindi un muscolo colorato
  // convive con quello, non con lo sfondo di pagina. Alzarlo costa
  // distinguibilita' fra gradini: misurato, portarlo a 2.4 fa scendere la
  // peggior coppia a 11.7 e a 2.6 la fa crollare a 8.6. Si e' scelta la
  // distinguibilita', che e' l'informazione.
  // ⭐ E il rischio che quel numero suggerisce - «un gradino debole si legge
  // come NON lavorato» - e' stato misurato invece che temuto: contro
  // `surface2` il gradino piu' vicino sta a **25.6** di ΔE2000 sotto
  // deuteranopia (chiaro) e **27.0** (scuro). Non si confondono: e' basso il
  // contrasto di LUMINANZA, non la distinguibilita'. Il contrasto WCAG e' la
  // metrica per il testo, non per due tinte piene affiancate.
  heatmapRamp: {
    light: ["#4f8078", "#8a944f", "#e0a52f", "#c65f27", "#87301d"],
    dark: ["#c8e8de", "#b3c07d", "#bf8b23", "#b66036", "#a24a47"],
  },
  // Rampa monocroma sul terracotta. Monotona in luminanza (chiara: pallido ->
  // profondo; scura: spenta -> accesa) e ogni gradino porta il proprio
  // inchiostro, perche' un solo inchiostro non passa su tutta la banda.
  effortRamp: {
    light: [
      { fill: "#f6e1d2", on: "#3a1c0c" },
      { fill: "#e8bc9c", on: "#3a1c0c" },
      { fill: "#d18f61", on: "#2c1408" },
      { fill: "#a05222", on: "#fff6ee" },
      { fill: "#6f3714", on: "#fff6ee" },
    ],
    dark: [
      { fill: "#4a2411", on: "#f3e3d6" },
      { fill: "#6d371a", on: "#f3e3d6" },
      { fill: "#96512a", on: "#f9ece2" },
      { fill: "#c07a4b", on: "#1b1109" },
      { fill: "#dfa878", on: "#1b1109" },
    ],
  },
};
